import * as React from 'react'
import { useMemo, useRef, useState } from 'react'
import { Button, Drawer, Well } from '@pushly/aqe/lib/components'
import { CheckCircleOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Divider, Flex, Form, notification, Select, Skeleton, Switch, Tag, Tooltip } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import CopyToClipboard from 'react-copy-to-clipboard'
import { simpleNotification } from '@halo/_utils/notifications'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { AppState } from '@halo/stores/app.state'
import cloneDeep from 'clone-deep'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { DomainDto } from '@halo/dtos/domain.dto'
import { DomainService } from '@halo/services/domain.service'
import * as randomstring from 'randomstring'
import { PlatformFlagEntities } from '@halo/enums/platform-flag-entities'
import { FlagModel } from '@halo/models/flag.model'
import { arraySortByModelGetter } from '@halo/_utils/array'
import FlagList from '@halo/structs/flag-list'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asDomainSubject } from '@halo/stores/ability.state'
import { getCollapseKeyTypeLabel, normalizeCollapseKeyType } from '@pushly/aqe/lib/utils/notification-collapse-key'

interface IDomainServerEditor {
    domain: DomainDto
    flags: FlagList
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDomainServerEditorFormValues {
    collapseKeyType?: string
    flags?: string[]
}

const computeChangedDomainValues = (
    domain: DomainDto,
    formValues: IDomainServerEditorFormValues,
): Partial<DomainDto> => {
    const values: Partial<DomainDto> = {}

    values.flags = (formValues.flags || []).map((flag) => flag.toUpperCase())
    const valueFlags = cloneDeep(values.flags).sort().join(',')
    const domainFlags = cloneDeep(domain.flags || [])
        .sort()
        .join(',')
    if (valueFlags === domainFlags) {
        delete values.flags
    }

    const currentCollapseKeyType = normalizeCollapseKeyType(domain.collapse_key_type)
    const selectedCollapseKeyType = normalizeCollapseKeyType(formValues.collapseKeyType)

    values.collapse_key_type = selectedCollapseKeyType ?? currentCollapseKeyType
    if (values.collapse_key_type === currentCollapseKeyType) {
        delete values.collapse_key_type
    }

    return values
}

export const DomainServerEditor: React.FC<IDomainServerEditor> = (props) => {
    const appState = useMemo(() => Container.get(AppState), [])
    const domainService = useMemo(() => Container.get(DomainService), [])

    const [form] = Form.useForm<IDomainServerEditorFormValues>()
    const [rawFlagsVisible, setRawFlagsVisible] = useState(false)

    const visibleFlagRefs = useRef<Record<string, any>>({})
    const devFlagRefs = useRef<Record<string, any>>({})

    const { domain, flags, visible, onClose, onDomainUpdated } = props

    // Subscribe to value changes so the wrapping Drawer's disableSubmit recomputes as the user edits.
    Form.useWatch([], form)

    const currentUserIsDev = appState.currentUser?.isInternalDevUser || false
    const currentValues = computeChangedDomainValues(domain, form.getFieldsValue())
    const initialCollapseKeyType = normalizeCollapseKeyType(domain.collapse_key_type) ?? domain.collapse_key_type

    const allActiveFlags = flags?.getActiveFlags()
    const exposedFlags: FlagModel[] = !allActiveFlags
        ? []
        : arraySortByModelGetter(
              allActiveFlags.filter((flag: FlagModel) => !flag.getIsDevOnly()),
              'getDisplayName',
          )
    const devOnlyFlags: FlagModel[] = !allActiveFlags
        ? []
        : arraySortByModelGetter(
              allActiveFlags.filter((flag: FlagModel) => flag.getIsDevOnly() && flag.getIsActive()),
              'getDisplayName',
          )

    const updateFlagToggles = (flagKeys: string[]): void => {
        Object.keys(visibleFlagRefs.current).forEach((flag) => {
            const ref = visibleFlagRefs.current[flag]

            if (ref?.rcSwitch) {
                ref.rcSwitch.setState({ checked: flagKeys.includes(flag) })
            }
        })

        Object.keys(devFlagRefs.current).forEach((flag) => {
            const ref = devFlagRefs.current[flag]

            if (ref?.rcSwitch) {
                ref.rcSwitch.setState({ checked: flagKeys.includes(flag) })
            }
        })
    }

    const updateRawFlags = (flag: FlagModel, enabled: boolean): void => {
        const currentFlags: string[] = form.getFieldValue('flags') ?? []
        let formFlags: string[]

        if (enabled && !currentFlags.includes(flag.getKey())) {
            formFlags = [...currentFlags, flag.getKey()]
        } else if (!enabled && currentFlags.includes(flag.getKey())) {
            formFlags = currentFlags.filter((f) => f !== flag.getKey())
        } else {
            formFlags = currentFlags
        }

        form.setFieldsValue({ flags: formFlags })
        updateFlagToggles(formFlags)
    }

    const handleSubmit = async () => {
        try {
            await form.validateFields()
            const mergedValues = computeChangedDomainValues(domain, form.getFieldsValue())

            const { ok, data } = await domainService.patch(domain.id, mergedValues, {
                showLoadingScreen: true,
            })

            if (ok) {
                onDomainUpdated(data)
                onClose(null)
            }
        } catch (err) {
            // v6 validateFields rejects with { errorFields, values, outOfDate };
            // aqe's simpleFormErrorNotification handles both legacy and v6 shapes.
            simpleFormErrorNotification(err)
        }
    }

    return (
        <Drawer
            className={classnames('ddf-server-editor')}
            title="Update Server Keys & Flags"
            placement="right"
            closable={true}
            width={800}
            onSubmit={handleSubmit}
            disableSubmit={Object.keys(currentValues).length === 0}
            onClose={onClose}
            open={visible}
        >
            <Form
                form={form}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                initialValues={{
                    collapseKeyType: initialCollapseKeyType,
                    flags: [...(domain.flags || [])],
                }}
                onFinish={handleSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Well title="Notification Keys" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item
                        label="Collapse Key"
                        name="collapseKeyType"
                        className={classnames('ddf-server-editor-ck')}
                    >
                        <Select>
                            <Select.Option value="DOMAIN_ID">Domain Level</Select.Option>
                            <Select.Option value="NOTIFICATION_SCHEDULE_ID">Notification Level</Select.Option>
                        </Select>
                    </Form.Item>
                </Well>

                <Divider />

                <Well title="Flags" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item label="Service & Feature Flags">
                        <div className={classnames(null, 'flag-toggles')}>
                            {exposedFlags.map((flag, idx) => {
                                const flagsPerRow = 2
                                const shouldBreak = idx >= flagsPerRow && idx % flagsPerRow === 0

                                return (
                                    <React.Fragment key={idx}>
                                        {shouldBreak && <div className="break" />}
                                        <label className="flag-toggle">
                                            <Switch
                                                ref={(el) => (visibleFlagRefs.current[flag.getKey()] = el)}
                                                size="small"
                                                defaultChecked={domain.flags?.includes(flag.getKey())}
                                                onChange={(enabled) => updateRawFlags(flag, enabled)}
                                            />
                                            <Tooltip title={flag.getDescription() || flag.getDisplayName()}>
                                                <span>{flag.getDisplayName() ?? flag.getKey()}</span>
                                            </Tooltip>
                                        </label>
                                    </React.Fragment>
                                )
                            })}
                        </div>
                    </Form.Item>
                </Well>

                <Divider />

                <Well
                    className={classnames(null, {
                        'hide-visibility': !currentUserIsDev,
                    })}
                    title="Additional Flags (Dev Access Only)"
                    mode="ghost"
                    footerMode="hidden"
                    padding="none"
                >
                    <Form.Item label="Feature Flags">
                        <div className={classnames(null, 'flag-toggles')}>
                            {devOnlyFlags.map((flag, idx) => {
                                const flagsPerRow = 2
                                const shouldBreak = idx >= flagsPerRow && idx % flagsPerRow === 0

                                return (
                                    <React.Fragment key={idx}>
                                        {shouldBreak && <div className="break" />}
                                        <label className="flag-toggle">
                                            <Switch
                                                ref={(el) => (devFlagRefs.current[flag.getKey()] = el)}
                                                size="small"
                                                defaultChecked={domain.flags?.includes(flag.getKey())}
                                                onChange={(enabled) => updateRawFlags(flag, enabled)}
                                            />
                                            <Tooltip title={flag.getDescription() || flag.getDisplayName()}>
                                                <span>{flag.getDisplayName() ?? flag.getKey()}</span>
                                            </Tooltip>
                                        </label>
                                    </React.Fragment>
                                )
                            })}
                        </div>
                    </Form.Item>

                    <Form.Item label="Raw Flag Editor">
                        <Flex gap={8} align="center">
                            <Switch size="small" checked={rawFlagsVisible} onChange={(v) => setRawFlagsVisible(v)} />
                            <Form.Item name="flags" noStyle={true}>
                                <Select<string[]>
                                    style={
                                        rawFlagsVisible ? { flex: 1 } : { position: 'absolute', visibility: 'hidden' }
                                    }
                                    mode="tags"
                                    showSearch={false}
                                    tokenSeparators={[',', ';', ' ', '\n']}
                                    onChange={updateFlagToggles}
                                />
                            </Form.Item>
                        </Flex>
                    </Form.Item>
                </Well>
            </Form>
        </Drawer>
    )
}

interface IDomainServerWell {
    loading?: boolean
    domain: DomainDto
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDSWState {
    showDrawer: boolean
    drawerKey?: string
    flags?: FlagList
}

export class DomainServerWell extends React.Component<IDomainServerWell, IDSWState> {
    public state: IDSWState = {
        showDrawer: false,
        flags: FlagList.from([]),
    }

    protected appState: AppState
    protected appService: AppService

    public constructor(props: IDomainServerWell) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public componentDidMount() {
        this.getActiveFlags()
    }

    public render() {
        return (
            <Well
                className="type-3 nested server-well"
                title="Server Keys & Flags"
                actions={
                    this.appState.abilityStore.can(AbilityAction.UPDATE, asDomainSubject(this.props.domain)) && (
                        <Button key="edit" size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                            <EditOutlined />
                            <span>Edit</span>
                        </Button>
                    )
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderNotificationWell()}
                            {this.renderFlagsWell()}

                            <DomainServerEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.props.domain}
                                flags={this.state.flags}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderNotificationWell() {
        const currentUserIsDev = this.appState.currentUser?.isInternalDevUser || false

        return (
            <Well.Section title="Notification Keys">
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Collapse Key</div>
                    <div className={classnames('form-layout-row-value', 'can-copy')}>
                        <CopyToClipboard
                            onCopy={() => this.handleCopied('collapse key')}
                            text={this.domain.collapse_key_type}
                        >
                            <span>{getCollapseKeyTypeLabel(this.domain.collapse_key_type)}</span>
                        </CopyToClipboard>
                    </div>
                </div>
            </Well.Section>
        )
    }

    protected renderFlagsWell() {
        const actingUserIsDevUser = this.appState.currentUser?.isInternalDevUser ?? false

        const allFlags = this.state.flags
        const exposedFlags = allFlags.getActiveFlags().filter((flag: FlagModel) => !flag.getIsDevOnly())
        const sortedFlags = arraySortByModelGetter(exposedFlags, 'getDisplayName')

        return (
            <Well.Section title="Flags">
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Service & Feature Flags</div>
                    <div className={classnames('form-layout-row-value flag-toggles')}>
                        {sortedFlags
                            .filter((f) => this.domain.flags?.includes(f.getKey()))
                            .map((flag, idx) => {
                                const flagsPerRow = 3
                                const shouldBreak = idx >= flagsPerRow && idx % flagsPerRow === 0

                                return (
                                    <React.Fragment key={idx}>
                                        {shouldBreak && <div className="break" />}
                                        <Tooltip title={flag.getDescription()}>
                                            <span className="flag-toggle">
                                                <CheckCircleOutlined className="flag-enabled" />
                                                <span>{flag.getDisplayName() ?? flag.getKey()}</span>
                                            </span>
                                        </Tooltip>
                                    </React.Fragment>
                                )
                            })}
                    </div>
                </div>

                {actingUserIsDevUser && (
                    <>
                        <br />
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>
                                All Flags (Dev Access Only)
                                <div className={classnames('form-layout-row-sub')}>
                                    <InfoCircleOutlined />
                                    <span>
                                        View{' '}
                                        <a
                                            href="https://pushly.atlassian.net/wiki/spaces/AQ/pages/372080658/Flags#Domain-Flags"
                                            target="_blank"
                                        >
                                            Available Tags
                                        </a>
                                    </span>
                                </div>
                            </div>
                            <div className={classnames('form-layout-row-value')}>
                                {this.domain.flags?.map((flagKey, idx) => (
                                    <Tag
                                        key={idx}
                                        className={classnames('entity-flag-tag', {
                                            unknown: !allFlags.findByKey(flagKey),
                                            deprecated: allFlags.findByKey(
                                                flagKey,
                                                (flag: FlagModel) => !flag.getIsActive(),
                                            ),
                                        })}
                                    >
                                        {flagKey}
                                    </Tag>
                                )) || 'No flags'}
                            </div>
                        </div>
                    </>
                )}
            </Well.Section>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected getActiveFlags = async () => {
        const query = {
            entities: [PlatformFlagEntities.DOMAIN, PlatformFlagEntities.DEMO_DOMAIN].join(','),
            pagination: '0',
        }

        let { data: flags } = await this.appService.fetchFlags({ query })
        flags = FlagList.from(flags.map((f: any) => FlagModel.build(f)))

        return this.setState({ flags })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
